import { useEffect, useState } from 'react';
import {
  AlertCircle,
  BadgeDollarSign,
  Check,
  Hash,
  Loader2,
  Save,
  Sparkles,
  Star,
  X,
} from 'lucide-react';
import { StringListField } from '../../components/ui/StringListField';
import { useCreatePlan, useUpdatePlan } from '../../services/httpServices/queries';
import type { WebsitePlan } from '../../services/httpServices/websiteService';

interface WebsitePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The plan being edited, or null/undefined when adding a new one. */
  plan?: WebsitePlan | null;
  onSaved?: () => void;
}

interface PlanForm {
  name: string;
  price: string;
  description: string;
  features: string[];
  popular: boolean;
  isActive: boolean;
  displayOrder: number;
}

const EMPTY_FORM: PlanForm = {
  name: '',
  price: '',
  description: '',
  features: [],
  popular: false,
  isActive: true,
  displayOrder: 0,
};

function validate(form: PlanForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = 'A plan name is required.';
  if (!form.price.trim()) errors.price = 'A price is required.';
  return errors;
}

/**
 * Add/edit dialog for a single pricing plan. Plans are rendered in the home
 * page "Plans & Pricing" section, so the right-hand pane previews the card.
 */
export function WebsitePlanModal({ isOpen, onClose, plan, onSaved }: WebsitePlanModalProps) {
  const isEdit = !!plan?.id;
  const createMutation = useCreatePlan();
  const updateMutation = useUpdatePlan();

  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm(
      plan
        ? {
            name: plan.name ?? '',
            price: plan.price ?? '',
            description: plan.description ?? '',
            features: plan.features ?? [],
            popular: plan.popular ?? false,
            isActive: plan.isActive,
            displayOrder: plan.displayOrder ?? 0,
          }
        : EMPTY_FORM,
    );
    setErrors({});
    setGeneralError('');
  }, [isOpen, plan]);

  const setField = <K extends keyof PlanForm>(field: K, value: PlanForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setGeneralError('');
    const payload = {
      name: form.name.trim(),
      price: form.price.trim(),
      description: form.description.trim() || undefined,
      features: form.features,
      popular: form.popular,
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder) || 0,
    };

    try {
      if (isEdit && plan) {
        await updateMutation.mutateAsync({ id: plan.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      setGeneralError(
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.',
      );
    }
  };

  if (!isOpen) return null;

  const inputBase =
    'w-full px-3 py-2 border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 transition-colors';
  const ic = (field: string) =>
    `${inputBase} ${errors[field] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-primary'}`;
  const labelClass = 'block text-sm font-medium text-text mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <BadgeDollarSign size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">
                {isEdit ? 'Edit plan' : 'Add plan'}
              </h2>
              <p className="text-xs text-text-muted">
                Shown in the "Plans & Pricing" section on the home page.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {generalError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
              <AlertCircle size={16} />
              <span>{generalError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Left: fields */}
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Plan name *</label>
                <input
                  type="text"
                  autoFocus
                  className={ic('name')}
                  placeholder="e.g. Individual Plans"
                  value={form.name}
                  maxLength={150}
                  onChange={(e) => setField('name', e.target.value)}
                />
                {errors.name && <p className="text-xs text-danger mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className={labelClass}>Price *</label>
                <input
                  type="text"
                  className={ic('price')}
                  placeholder="e.g. $30"
                  value={form.price}
                  maxLength={50}
                  onChange={(e) => setField('price', e.target.value)}
                />
                {errors.price && <p className="text-xs text-danger mt-1">{errors.price}</p>}
              </div>

              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  rows={3}
                  className={ic('description')}
                  placeholder="Short description shown under the price"
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                />
              </div>

              <StringListField
                label="Features (one per line)"
                value={form.features}
                onChange={(items) => setField('features', items)}
                placeholder={'Routine Checkups\nMedical Specialist'}
                rows={5}
                helperText="Each line becomes a checklist item on the pricing card."
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Popular badge</label>
                  <button
                    type="button"
                    onClick={() => setField('popular', !form.popular)}
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      form.popular
                        ? 'border-primary/40 bg-primary/10 text-primary'
                        : 'border-border bg-background text-text-muted'
                    }`}
                  >
                    <span>{form.popular ? 'Popular' : 'Standard'}</span>
                    <span
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                        form.popular ? 'bg-primary' : 'bg-border'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          form.popular ? 'translate-x-[18px]' : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                  </button>
                </div>
                <div>
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <Hash size={13} /> Display order
                    </span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    className={inputBase + ' border-border focus:ring-primary'}
                    value={form.displayOrder}
                    onChange={(e) => setField('displayOrder', Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Status</label>
                <button
                  type="button"
                  onClick={() => setField('isActive', !form.isActive)}
                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    form.isActive
                      ? 'border-success/40 bg-success/10 text-success'
                      : 'border-border bg-background text-text-muted'
                  }`}
                >
                  <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                  <span
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                      form.isActive ? 'bg-success' : 'bg-border'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        form.isActive ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                </button>
              </div>
            </div>

            {/* Right: live preview */}
            <div className="space-y-2">
              <label className={labelClass}>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles size={13} className="text-primary" /> Live preview
                </span>
              </label>
              <div className="rounded-xl border border-border bg-background p-5">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-text">
                    {form.name.trim() || 'Plan name'}
                  </h3>
                  {form.popular && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      <Star size={10} fill="currentColor" /> Popular
                    </span>
                  )}
                </div>
                <div className="my-3 flex items-end gap-1.5">
                  <span className="text-3xl font-bold text-primary">
                    {form.price.trim() || '$0'}
                  </span>
                </div>
                {form.description.trim() && (
                  <p className="text-xs text-text-muted mb-3">{form.description.trim()}</p>
                )}
                <ul className="space-y-1.5">
                  {(form.features.length ? form.features : ['Feature one', 'Feature two']).map(
                    (feature, index) => (
                      <li
                        key={`${feature}-${index}`}
                        className="flex items-start gap-2 text-sm text-text-muted"
                      >
                        <Check size={14} className="mt-0.5 text-primary shrink-0" />
                        {feature}
                      </li>
                    ),
                  )}
                </ul>
              </div>
              <p className="text-xs text-text-muted">
                {form.isActive
                  ? 'Visible in the home page "Plans & Pricing" section.'
                  : 'Hidden from the home page "Plans & Pricing" section.'}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {isEdit ? 'Save changes' : 'Add plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
